/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import {
  ContactNode,
  ContactParams,
  Course,
  CourseClass,
  Product,
  PromotionParams,
  V2SuggestionRequest,
  V2SuggestionResponse
} from '@api';
import { chunk } from 'lodash';
import uniq from 'lodash/uniq';
import { normalize } from 'normalizr';
import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { bufferTime, catchError, concat, defer, filter, forkJoin, from, map, mergeMap, retry, tap } from 'rxjs';
import CheckoutServiceV2 from 'src/js/enrol/services/CheckoutApiService';
import { ProductEvent, sendViewItemEvent, sendViewItemListEvent } from 'src/js/services/CommonAnalyticsService';
import { IshAction } from '../../actions/IshAction';
import { FULFILLED } from '../../common/actions/ActionUtils';
import { mapError, mapPayload } from '../../common/epics/EpicUtils';
import { changePhase, getCheckoutModelFromBackend } from '../../enrol/actions/Actions';
import * as ContactAddActions from '../../enrol/containers/contact-add/actions/Actions';
import {
  REWRITE_CONTACT_NODE_TO_STATE,
  rewriteContactNodeToState
} from '../../enrol/containers/summary/actions/Actions';
import { Phase } from '../../enrol/reducers/State';
import { BuildContactNodeRequest } from '../../enrol/services/CheckoutService';
import { Injector } from '../../injector';
import {
  ClassesListSchema,
  ClassesSchema,
  InactiveCoursesListSchema,
  ProductsListSchema,
  ProductsSchema,
  PromotionsSchema,
  WaitingCoursesListSchema,
  WaitingCoursesSchema,
} from '../../NormalizeSchema';
import { IshState } from '../../services/IshState';
import {
  Actions,
  addClassToCart,
  addProductToCart,
  addWaitingCourseToCart,
  requestCourseClass,
  requestProduct
} from '../actions/Actions';

const {
  courseClassesApi,
  productsApi,
  promotionApi,
  contactApi,
  mergeService,
  legacySyncStorage,
  suggestionsApi,
} = Injector;

export const WebEpic = combineEpics(
  createCoursesEpic(),
  createCoursesClassEpic(),
  createWaitingCoursesEpic(),
  createInactiveCoursesEpic(),
  createUpdateCoursesEpic(),
  createProductsEpic(),
  createUpdateProductsEpic(),
  createPromotionsEpic(),
  createContactEpic(),
  createSyncCartRequestEpic(),
  createSyncCartEpic(),
  createLegacySyncEpic(),
  createAddClassToCartEpic(),
  createAddPurchaseToSummaryEpic(),
  createRemoveClassFromCartEpic(),
  createAddProductToCartEpic(),
  createRemoveProductFromCartEpic(),
  createAddPromotionToCartEpic(),
  createRemovePromotionFromCartEpic(),
  createAddWaitingCourseToCartEpic(),
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  createRemoveWaitingCourseFromCartEpic(),
  createSuggestionsEpic(),
  createModelUpdateEpic(),
  createEcommerceEventsEpic()
);

function createCoursesClassEpic() {
  return (action$, store: StateObservable<IshState>) => action$.pipe(
    ofType(Actions.REQUEST_COURSE_CLASS),
    bufferTime(100), // batch actions
    filter<IshAction[]>(actions => Boolean(actions.length)),
    mergeMap<IshAction[], any>((actions) => {
      const ids = {};
      const codes = {};

      actions.forEach((action) => {
        const { id, code, addToCart } = action.payload;
        if (id) {
          ids[id] = addToCart;
        }
        if (code) {
          codes[code] = addToCart;
        }
      });

      const chunkIds = chunk(uniq(Object.keys(ids)), 25);
      const chunkCodes = chunk(uniq(Object.keys(codes)), 25);

      const courseStream = concat(
        from(chunkIds).pipe(
          mergeMap((groupedIds: string[]) => defer(() => courseClassesApi.getCourseClasses({
            courseClassesIds: groupedIds,
            contact: createContactParams(store.value),
            promotions: createPromotionParams(store.value)
          }))
          ),
          retry(2)
        ),
        from(chunkCodes).pipe(
          mergeMap((groupedCodes: string[]) => defer(() => courseClassesApi.getCourseClasses({
            courseClassesCodes: groupedCodes,
            contact: createContactParams(store.value),
            promotions: createPromotionParams(store.value),
          }))),
          retry(2)
        )).pipe(
        mergeMap<CourseClass[], any>((payload: CourseClass[]) => {

          const addToCartActions = [];

          payload.forEach((courseClass) => {
            if (ids[courseClass.id] || codes[`${courseClass.course?.code}-${courseClass.code}`]) addToCartActions.push(addClassToCart(courseClass));
          });

          if (addToCartActions.length) {
            addToCartActions.push(store.value.checkout.payerId
              ? changePhase(Phase.Summary)
              : changePhase(Phase.AddPayer));
          }

          return [
            mapPayload(Actions.REQUEST_COURSE_CLASS)(normalize(payload, ClassesListSchema)),
            ...addToCartActions
          ];
        }),
        catchError(mapError(Actions.REQUEST_COURSE_CLASS))
      );
        
    

      const urlParamContact = checkUrlParamContact();

      return urlParamContact
        ? concat(
          from(contactApi.getContact(urlParamContact)).pipe(
            map(mapPayload(Actions.REQUEST_CONTACT)),
            catchError(mapError(Actions.REQUEST_CONTACT))
          ),
          courseStream
        ) : courseStream;
    }));
}

function createWaitingCoursesEpic() {
  return (action$) => action$.pipe(
    ofType(Actions.REQUEST_WAITING_COURSE),
    bufferTime(100), // batch actions
    filter<IshAction[]>((actions) => Boolean(actions.length)),
    mergeMap<IshAction[], any>((actions) => {
      const ids = actions.reduce((prev, action) => {
        const { id, addToCart } = action.payload;
        prev[id] = addToCart;
        return prev;
      }, {});

      return defer(() => courseClassesApi.getCourses({
        coursesIds: uniq(Object.keys(ids)),
      })).pipe(
        retry(2), // Retry two times if request has been rejected
        mergeMap((payload: Course[]) => {
          const addToCartActions = [];
  
          payload.forEach((course) => {
            if (ids[course.id]) addToCartActions.push(addWaitingCourseToCart(course));
          });
  
          return [
            mapPayload(Actions.REQUEST_WAITING_COURSE)(normalize(payload, WaitingCoursesListSchema)),
            ...addToCartActions
          ];
        }),
        catchError(mapError(Actions.REQUEST_WAITING_COURSE)) 
      );
    }) 
  );
}


function createCoursesEpic() {
  return (action$, store: StateObservable<IshState>) => action$.pipe(
    ofType(Actions.REQUEST_COURSE),
    bufferTime(100), // batch actions
    filter<IshAction[]>((actions) => Boolean(actions.length)),
    mergeMap<IshAction[], any>((actions) => {
      const ids = {};
      const codes = {};

      actions.forEach((action) => {
        const { id, code, addToCart } = action.payload;
        if (id) {
          ids[id] = addToCart;
        }
        if (code) {
          codes[code] = addToCart;
        }
      });

      const coursesIds = uniq(Object.keys(ids));
      const coursesCodes = uniq(Object.keys(codes));

      return concat(
        forkJoin([
          ...coursesIds.map(id => courseClassesApi.getAvailableClasses(id))
        ]),
        coursesCodes.length
          ? from(courseClassesApi.getCourses({ coursesCodes })).pipe(
            mergeMap(courses => forkJoin(courses.map(c => courseClassesApi.getAvailableClasses(c.id))))
          )
          : []
      ).pipe(
        retry(2), // Retry two times if request has been rejected
        mergeMap((payload: CourseClass[][]) => {
          const addToCartActions = [];
          const classes = [];
  
          payload.forEach((courseClasses) => {
            classes.push(...courseClasses);
            if (ids[courseClasses[0]?.course.id]) {
              addToCartActions.push(addClassToCart(courseClasses[0]));
            }
            if (codes[courseClasses[0]?.course.code]) {
              addToCartActions.push(addClassToCart(courseClasses[0]));
            }
          });
  
          if (addToCartActions.length) {
            addToCartActions.push(store.value.checkout.payerId
              ? changePhase(Phase.Summary)
              : changePhase(Phase.AddPayer));
          }
  
          return [
            mapPayload(Actions.REQUEST_COURSE_CLASS)(normalize(classes, ClassesListSchema)),
            ...addToCartActions
          ];
        }),
        catchError(mapError(Actions.REQUEST_COURSE_CLASS))
      );
    })
  );
}

function createInactiveCoursesEpic() {
  return (action$) => action$.pipe(
    ofType(Actions.REQUEST_INACTIVE_COURSE),
    bufferTime(100), // batch actions
    filter<IshAction[]>((actions) => Boolean(actions.length)),
    mergeMap<IshAction[], any>((actions) => {
      const ids: string[] = actions.map((action) => action.payload);
      return defer(() => courseClassesApi.getCourses({
        coursesIds: uniq(ids),
      })).pipe(
        retry(2), // Retry two times if request has been rejected
        map((payload) => normalize(payload, InactiveCoursesListSchema)),
        map(mapPayload(Actions.REQUEST_INACTIVE_COURSE)),
        catchError(mapError(Actions.REQUEST_INACTIVE_COURSE))
      );
    })
  );
}

function createProductsEpic() {
  return (action$, store: StateObservable<IshState>) => action$.pipe(
    ofType(Actions.REQUEST_PRODUCT),
    bufferTime(100), // batch actions
    filter<IshAction[]>((actions) => Boolean(actions.length)),
    mergeMap<IshAction[], any>((actions) => {
      const ids = {};
      const codes = {};

      actions.forEach((action) => {
        const { id, code, addToCart } = action.payload;
        if (id) {
          ids[id] = addToCart;
        }
        if (code) {
          codes[code] = addToCart;
        }
      });

      return from(productsApi.getProducts({
        productsIds: uniq(Object.keys(ids)),
        productsCodes: uniq(Object.keys(codes)),
        contact: createContactParams(store.value),
        promotions: createPromotionParams(store.value),
      })).pipe(
        mergeMap((payload: Product[]) => {
          const productActions = [];

          payload.forEach((product) => {
            if (ids[product.id] || codes[product.code]) productActions.push(addProductToCart(product));
          });

          if (productActions.length) {
            productActions.push(store.value.checkout.payerId
              ? changePhase(Phase.Summary)
              : changePhase(Phase.AddPayer));
          }

          return [
            mapPayload(Actions.REQUEST_PRODUCT)(normalize(payload, ProductsListSchema)),
            ...productActions
          ];
        }),
        catchError(mapError(Actions.REQUEST_PRODUCT))
      );
    })
  );
}

function createSuggestionsEpic() {
  return (action$, store: StateObservable<IshState>) => action$.pipe(
    ofType(
      Actions.REQUEST_SUGGESTION,
      Actions.ADD_PRODUCT_TO_CART,
      Actions.ADD_CLASS_TO_CART,
      Actions.REMOVE_CLASS_FROM_CART,
      Actions.REMOVE_PRODUCT_FROM_CART
    ),
    bufferTime(100), // batch actions
    filter<IshAction[]>((actions) => Boolean(actions.length)),
    mergeMap(() => {
      const state = store.value;
      const request:V2SuggestionRequest = {
        courseIds: state.cart.courses.result
          .map((id) => state.cart.courses.entities[id]?.course.id),
        productIds: state.cart.products.result
      };

      if (!request.courseIds.length && !request.productIds.length) {
        return [];
      }

      return from(suggestionsApi.getSuggestion(request)).pipe(
        mergeMap((payload: V2SuggestionResponse) => [
          ...payload.products.map((pId) => requestProduct({ id: pId })),
          ...payload.courseClasses.map((cId) => requestCourseClass({ id: cId })),
          mapPayload(Actions.REQUEST_SUGGESTION)(payload)
        ]),
        catchError(mapError(Actions.REQUEST_SUGGESTION))
      );
    })
  );
}

function createModelUpdateEpic() {
  return (action$) => action$.pipe(
    ofType(
      REWRITE_CONTACT_NODE_TO_STATE
    ),
    bufferTime(1000), // batch actions
    filter<IshAction[]>((actions) => Boolean(actions.length)),
    mergeMap(() => [getCheckoutModelFromBackend()])
  );
}

function createPromotionsEpic() {
  return (action$) => action$.pipe(
    ofType(Actions.REQUEST_PROMOTION),
    mergeMap<IshAction, any>((action) => from(promotionApi.getPromotion(action.payload)).pipe(
      map(mapPayload(Actions.REQUEST_PROMOTION)),
      catchError(mapError(Actions.REQUEST_PROMOTION))
    ))
  );
}

function createContactEpic() {
  return (action$) => action$.pipe(
    ofType(Actions.REQUEST_CONTACT),
    mergeMap<IshAction, any>((action) => from(contactApi.getContact(action.payload)).pipe(
      map(mapPayload(Actions.REQUEST_CONTACT)),
      catchError(mapError(Actions.REQUEST_CONTACT))
    ))
  );
}

function createUpdateCoursesEpic() {
  return (action$, store: StateObservable<IshState>) => action$.pipe(
    ofType(
      FULFILLED(Actions.REQUEST_CONTACT),
      FULFILLED(Actions.ADD_PROMOTION_TO_CART),
      FULFILLED(Actions.REMOVE_PROMOTION_FROM_CART),
    ),
    filter(() => store.value.courses.result.length),
    mergeMap(() => {
      const ids = store.value.courses.result;

      return from(courseClassesApi.getCourseClasses({
        courseClassesIds: ids,
        contact: createContactParams(store.value),
        promotions: createPromotionParams(store.value),
      })).pipe(
        map((payload) => normalize(payload, ClassesListSchema)),
        map(mapPayload(Actions.REQUEST_COURSE_CLASS)),
        catchError(mapError(Actions.REQUEST_COURSE_CLASS))
      );
    })
  );
}

function createUpdateProductsEpic() {
  return (action$, store: StateObservable<IshState>) => action$.pipe(
    ofType(
      FULFILLED(Actions.REQUEST_CONTACT),
      FULFILLED(Actions.ADD_PROMOTION_TO_CART),
      FULFILLED(Actions.REMOVE_PROMOTION_FROM_CART),
    ),
    filter(() => store.value.products.result.length),
    mergeMap(() => {
      const ids = store.value.products.result;

      return from(productsApi.getProducts({
        productsIds: ids,
        contact: createContactParams(store.value),
        promotions: createPromotionParams(store.value),
      })).pipe(
        map((payload) => normalize(payload, ProductsListSchema)),
        map(mapPayload(Actions.REQUEST_PRODUCT)),
        catchError(mapError(Actions.REQUEST_PRODUCT))
      );
    })
  );
}

function createAddClassToCartEpic() {
  return (action$, store: StateObservable<IshState>) => action$.pipe(
    ofType(Actions.ADD_CLASS_TO_CART),
    map<IshAction, any>((action) => ({
      type: FULFILLED(Actions.ADD_CLASS_TO_CART),
      payload: normalize(store.value.courses.entities[action.payload.id] || {}, ClassesSchema),
    }))
  );
}

function createAddPurchaseToSummaryEpic() {
  return (action$, store: StateObservable<IshState>) => action$.pipe(
    ofType(
      FULFILLED(Actions.ADD_CLASS_TO_CART),
      FULFILLED(Actions.ADD_PRODUCT_TO_CART),
    ),
    mergeMap(() => {
      const state = store.value;
      return forkJoin(
        state.checkout.contacts.result.map((id) => {
          const request = BuildContactNodeRequest.fromCartItems(state, id);
          if (!request) {
            return Promise.resolve(null);
          }
          request.contactId = id;
          return CheckoutServiceV2.getContactNodeByNodeRequest(request);
        }),
      ).pipe(
        mergeMap((results: ContactNode[]) => results.map((node) => (node
          ? rewriteContactNodeToState(node)
          : null))),
        filter((a) => !!a)
      );
    })
  );
}

function createEcommerceEventsEpic() {
  return (action$) => action$.pipe(
    ofType(
      FULFILLED(Actions.REQUEST_COURSE_CLASS),
      // FULFILLED(Actions.REQUEST_WAITING_COURSE),
      FULFILLED(Actions.REQUEST_PRODUCT),
    ),
    mergeMap<IshAction, any>(({ payload, type }) => {
      const products: ProductEvent[] = [];
      payload.result.map(id => {
        switch (type) {
          case FULFILLED(Actions.REQUEST_COURSE_CLASS): {
            const c = payload.entities.classes[id];
            products.push({
              item_id: c.id,
              item_name: `${c.course.name} ${c.course.code} ${c.code}`,
              item_category: 'Course class',
              price: c.price.fee,
              quantity: 1
            });
            break;
          }
          case FULFILLED(Actions.REQUEST_PRODUCT): {
            const p = payload.entities.products[id];
            products.push({
              item_id: p.id,
              item_name: p.name,
              item_category: 'Product',
              price: p.price,
              quantity: 1
            });
          }
        }
      });

      if (window.location.href.match(/\/(course|product)\//)) {
        sendViewItemEvent(products);
      }

      if (window.location.href.match(/(\/courses|products)$/)) {
        sendViewItemListEvent(products);
      }
      return [];
    })
  );
}

function createRemoveClassFromCartEpic() {
  return (action$) => action$.pipe(
    ofType(Actions.REMOVE_CLASS_FROM_CART),
    map<IshAction, any>((action) => ({
      type: FULFILLED(Actions.REMOVE_CLASS_FROM_CART),
      payload: normalize(action.payload, ClassesSchema),
    }))
  );
}

function createAddWaitingCourseToCartEpic() {
  return (action$, store: StateObservable<IshState>) => action$.pipe(
    ofType(Actions.ADD_WAITING_COURSE_TO_CART),
    map<IshAction, any>((action) => ({
      type: FULFILLED(Actions.ADD_WAITING_COURSE_TO_CART),
      payload: normalize(store.value.waitingCourses.entities[action.payload.id], WaitingCoursesSchema),
    }))
  );
}

function createAddProductToCartEpic() {
  return (action$, store: StateObservable<IshState>) => action$.pipe(
    ofType(Actions.ADD_PRODUCT_TO_CART),
    map<IshAction, any>((action) => ({
      type: FULFILLED(Actions.ADD_PRODUCT_TO_CART),
      payload: normalize(store.value.products.entities[action.payload.id], ProductsSchema),
    }))
  );
}

function createRemoveProductFromCartEpic() {
  return (action$) => action$.pipe(
    ofType(Actions.REMOVE_PRODUCT_FROM_CART),
    map<IshAction, any>((action) => ({
      type: FULFILLED(Actions.REMOVE_PRODUCT_FROM_CART),
      payload: normalize(action.payload, ProductsSchema),
    }))
  );
}

function createRemoveWaitingCourseFromCartEpic() {
  return (action$) => action$.pipe(
    ofType(Actions.REMOVE_WAITING_COURSE_FROM_CART),
    map<IshAction, any>((action) => ({
      type: FULFILLED(Actions.REMOVE_WAITING_COURSE_FROM_CART),
      payload: normalize(action.payload, WaitingCoursesSchema),
    }))
  );
}

function createAddPromotionToCartEpic() {
  return (action$) => action$.pipe(
    ofType(Actions.ADD_PROMOTION_TO_CART),
    mergeMap<IshAction, any>((action) => from(promotionApi.getPromotion(action.payload)).pipe(
      map((payload) => normalize(payload, PromotionsSchema)),
      map(mapPayload(Actions.ADD_PROMOTION_TO_CART)),
      catchError(mapError(Actions.ADD_PROMOTION_TO_CART)))
    )
  );
}

function createRemovePromotionFromCartEpic() {
  return (action$) => action$.pipe(
    ofType(Actions.REMOVE_PROMOTION_FROM_CART),
    map<IshAction, any>((action) => ({
      type: FULFILLED(Actions.REMOVE_PROMOTION_FROM_CART),
      payload: normalize(action.payload, PromotionsSchema),
    }))
  );
}

function createSyncCartRequestEpic() {
  return (action$) => action$.pipe(
    ofType(
      FULFILLED(Actions.ADD_CLASS_TO_CART),
      FULFILLED(Actions.REMOVE_CLASS_FROM_CART),
      FULFILLED(Actions.ADD_PRODUCT_TO_CART),
      FULFILLED(Actions.REMOVE_PRODUCT_FROM_CART),
      FULFILLED(Actions.ADD_PROMOTION_TO_CART),
      FULFILLED(Actions.REMOVE_PROMOTION_FROM_CART),
      FULFILLED(Actions.REQUEST_PROMOTION),
      FULFILLED(Actions.REQUEST_CONTACT),
      ...Object.keys(Actions).map(a => Actions[a]),
      ContactAddActions.ADD_CONTACT_TO_STATE,
    ),
    map<IshAction, any>((action) => ({
      timestamp: new Date().getTime(),
      ...action,
    })),
    bufferTime(100),
    filter<IshAction[]>((actions) => Boolean(actions.length)),
    map((actions) => ({
      type: Actions.SYNC_CART,
      payload: actions,
    }))
  );
}

function createSyncCartEpic() {
  return (action$) => action$.pipe(
    ofType(Actions.SYNC_CART),
    mergeMap<IshAction, any>((action) => from(mergeService.merge(action)))
  );
}

function createLegacySyncEpic() {
  return (action$) => action$.pipe(
    ofType(
      FULFILLED(Actions.ADD_CLASS_TO_CART),
      FULFILLED(Actions.REMOVE_CLASS_FROM_CART),
      FULFILLED(Actions.ADD_PRODUCT_TO_CART),
      FULFILLED(Actions.REMOVE_PRODUCT_FROM_CART),
    ),
    tap(legacySyncStorage.sync),
    filter(() => false)
  );
}

function checkUrlParamContact() {
  const urlObj = new URL(window.location.href);
  return urlObj.searchParams.get('student');
}

function createContactParams(state: IshState): ContactParams {
  const { contact } = state.cart;

  if (!contact) {
    return null;
  }

  return {
    id: contact.id,
  };
}

function createPromotionParams(state: IshState): PromotionParams[] {
  return state.cart.promotions.result.map((promotionId) => ({
    id: promotionId,
  }));
}
